import type { User, IndividualRegistrationData, ProviderRegistrationData } from '@/lib/types';
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  registerIndividual: (data: IndividualRegistrationData) => Promise<any>;
  registerProvider: (data: ProviderRegistrationData) => Promise<any>;
  logout: () => Promise<void>;
  refreshVerificationStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session only on initial load
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('careVaultToken');
        const storedUser = localStorage.getItem('careVaultUser');

        if (token && storedUser) {
          // If we already have user data in state, don't override it
          // This prevents the useEffect from interfering with login flow
          if (!user) {
            try {
              // Parse stored user data first
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);

              // Optionally verify token is still valid in background
              // but don't override user state if it fails
              authAPI.getProfile().catch(() => {
                // If profile fetch fails silently, token might be expired
                // but don't immediately clear - let other API calls handle it
              });
            } catch (parseError) {
              // If stored user data is corrupted, clear storage
              localStorage.removeItem('careVaultToken');
              localStorage.removeItem('careVaultUser');
            }
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // Only clear storage if we don't have user data in state
        if (!user) {
          localStorage.removeItem('careVaultToken');
          localStorage.removeItem('careVaultUser');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []); // Remove dependencies to only run on mount

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      const { user: userData, session } = response.data;

      setUser(userData);
      localStorage.setItem('careVaultUser', JSON.stringify(userData));
      localStorage.setItem('careVaultToken', session.access_token);

      // Always navigate to dashboard - ProtectedRoute will handle showing
      // the verification overlay if email is not confirmed
      navigate('/app/dashboard');
    } catch (error) {
      throw error;
    }
  };



  const registerIndividual = async (data: IndividualRegistrationData) => {
    try {
      const response = await authAPI.registerIndividual(data);
      const { user: userData, session } = response.data;

      setUser(userData);
      if (session) {
        localStorage.setItem('careVaultUser', JSON.stringify(userData));
        localStorage.setItem('careVaultToken', session.access_token);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const registerProvider = async (data: ProviderRegistrationData) => {
    try {
      const response = await authAPI.registerProvider(data);
      const { user: userData, session } = response.data;

      setUser(userData);
      if (session) {
        localStorage.setItem('careVaultUser', JSON.stringify(userData));
        localStorage.setItem('careVaultToken', session.access_token);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('careVaultUser');
      localStorage.removeItem('careVaultToken');
      navigate('/login');
    }
  };

  /**
   * Refresh the user's email verification status from the server.
   * Updates the user state and localStorage if verification status has changed.
   * @returns true if email is now verified, false otherwise
   */
  const refreshVerificationStatus = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authAPI.getVerificationStatus();
      const { emailConfirmed } = response.data;

      if (user && emailConfirmed !== user.emailConfirmed) {
        // Update user state with new verification status
        const updatedUser = { ...user, emailConfirmed };
        setUser(updatedUser);
        localStorage.setItem('careVaultUser', JSON.stringify(updatedUser));
      }

      return emailConfirmed;
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
      return user?.emailConfirmed ?? false;
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      registerIndividual,
      registerProvider,
      logout,
      refreshVerificationStatus,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};